import axiosPublic from '@config/axios';
import axiosPortal, {
  clearStoredPortalSession,
  persistStoredPortalSession,
} from '@config/axiosPortal';
import apiUrls from '@constants/api-urls';
import type { LineOption } from '@lib/lineOptions';
import { capturePortalLogout, identifyPortalSession } from '@lib/monitoring';

export interface StoreDetails {
  _id: string;
  customer_portal_background_color: string | null;
  customer_portal_secondary_background_color: string | null;
  customer_portal_primary_text_color: string | null;
  customer_portal_secondary_text_color: string | null;
  customer_portal_button_background_color: string | null;
  customer_portal_button_text_color: string | null;
  customer_portal_logo: string | null;
  customer_portal_logo_size: string | number | null;
  store_logo: string | null;
  // Subscription details page banners and perks. Every field is absent from older backends,
  // and a banner or the perks section hides itself when the merchant hasn't set it.
  customer_portal_top_banner_url?: string | null;
  customer_portal_top_banner_link?: string | null;
  customer_portal_bottom_banner_url?: string | null;
  customer_portal_bottom_banner_link?: string | null;
  customer_portal_top_banner_size?: number | null;
  customer_portal_bottom_banner_size?: number | null;
  customer_portal_perks_heading?: string | null;
  customer_portal_perks?: string[] | null;
}

export interface MagicLinkRequestBody {
  email: string;
  domain: string;
}

export interface MagicLinkRequestResponse {
  success: boolean;
  message: string;
}

export const getStoreDetailsBySubdomain = async (domain: string) => {
  try {
    const response = await axiosPublic.get(apiUrls.customerPortal.getStoreDetailsByDomain, {
      params: { domain },
    });
    return { success: true, data: response.data };
  } catch (error: unknown) {
    const err = error as { response?: { status: number; data?: { detail?: string } } };
    return {
      success: false,
      status: err?.response?.status || 500,
      message: err?.response?.data?.detail || 'Unknown error',
    };
  }
};

export const requestMagicLink = async (
  body: MagicLinkRequestBody,
): Promise<MagicLinkRequestResponse> => {
  const response = await axiosPublic.post<MagicLinkRequestResponse>(
    apiUrls.customerPortal.requestAccess,
    body,
  );
  return response.data;
};

export const resendMagicLinkEmail = async (body: MagicLinkRequestBody) => {
  return requestMagicLink(body);
};

export type MagicLinkAuthErrorCode = 'link_expired' | 'link_invalid';

export interface MagicLinkAuthSuccessResponse {
  success: true;
  session_token: string;
  expires_at: string;
  customer_id: string;
  store_id: string;
  redirect_url: string;
}

export interface MagicLinkAuthFailureResponse {
  success: false;
  error: MagicLinkAuthErrorCode;
}

export type MagicLinkAuthResponse = MagicLinkAuthSuccessResponse | MagicLinkAuthFailureResponse;

export const authenticateMagicLink = async (token: string): Promise<MagicLinkAuthResponse> => {
  const response = await axiosPublic.post<MagicLinkAuthResponse>(
    apiUrls.customerPortal.authenticate,
    null,
    { params: { token } },
  );
  return response.data;
};

export const persistPortalSession = (auth: MagicLinkAuthSuccessResponse) => {
  persistStoredPortalSession(auth);
  identifyPortalSession({
    customerId: auth.customer_id,
    storeId: auth.store_id,
  });
};

export const clearPortalSession = () => {
  clearStoredPortalSession();
};

export const getCustomerSession = async () => {
  const response = await axiosPortal.get(apiUrls.customerPortal.session);
  return response.data;
};

export const logoutCustomer = async () => {
  try {
    await axiosPortal.post(apiUrls.customerPortal.logout);
  } finally {
    capturePortalLogout();
    clearPortalSession();
  }
};

export interface PortalSubscriptionLineItem {
  id: string;
  name: string;
  subtext: string | null;
  size: string | null;
  color: string | null;
  price: number;
  compare_price: number | null;
  currency: string;
  quantity: number;
  product_image: string | null;
  sku: string;
  selected_options: LineOption[]; // normalized in useSubscriptions; the wire may send a legacy dict
  whop_payment_id?: string | null;
  subscription?: {
    frequency: string;
    discount_percent: number | null;
    billing_period_days: number | null;
    recurring_price?: number | null; // per-unit renewal price; differs from `price` under split (v2) pricing
  };
}

export interface PortalSubscription {
  id: string;
  subscription_display_id: string;
  status: 'active' | 'paused' | 'canceled' | 'expired' | 'past due';
  amount: number;
  currency: string;
  original_amount: number;
  shipping?: number;
  frequency: string;
  billing_period_days: number | null;
  start_date: string | null;
  end_date: string | null;
  cancel_date: string | null;
  next_payment_date: string | null;
  created_at: string;
  whop_membership_id: string | null;
  whop_user_id: string | null;
  whop_member_id: string | null;
  line_item: PortalSubscriptionLineItem | null;
  cancel_at_period_end: boolean;
  // Pause fields (bounded "pause for X cycles" retention offer). pause_resumes_at is when the
  // subscription resumes and the next charge lands; null = indefinite pause.
  paused_at: string | null;
  pause_resumes_at: string | null;
  pause_cycles: number | null;
  // A retention discount Whop is expected to apply to the NEXT charge, else null. amount
  // stays the full price. Absent from older backends.
  active_discount?: PortalActiveDiscount | null;
  // Subscription rewards banner (LAS-514): the banner for the cycle this subscription has
  // reached, on every live subscription. Absent from older backends.
  reward?: PortalSubscriptionReward | null;
  // False when the pause endpoint would refuse (same checks as its 409s). Absent from
  // older backends.
  can_pause?: boolean;
  // Date of the latest successful payment, for the details page. Absent from older backends.
  last_payment_date?: string | null;
  // Every item on the subscription (line_item is only the first). Absent from older backends.
  line_items?: PortalSubscriptionLineItem[];
}

export interface PortalSubscriptionReward {
  cycle: number; // the configured banner's cycle: the highest one the subscriber has reached
  banner_image_url: string;
}

export interface PortalActiveDiscount {
  discount_percentage: number;
  discount_duration: CancellationDiscountDuration;
  discount_duration_months: number | null;
  full_amount_cents: number;
  // Expected, not confirmed: Whop's rounding may differ by a cent.
  discounted_amount_cents: number;
  currency: string;
}

export const getPortalSubscriptions = async (): Promise<PortalSubscription[]> => {
  const response = await axiosPortal.get(apiUrls.subscriptions.byPortalCustomer);
  return response.data;
};

export const cancelPortalSubscription = async (
  subscriptionId: string,
  cancellationMode: 'at_period_end' | 'immediate' = 'at_period_end',
) => {
  const response = await axiosPortal.post(
    `${apiUrls.subscriptions.byPortalCustomer}/${subscriptionId}/cancel`,
    { cancellation_mode: cancellationMode },
  );
  return response.data;
};

export const addFreeDaysToPortalSubscription = async (subscriptionId: string, freeDays: number) => {
  const response = await axiosPortal.post(
    `${apiUrls.subscriptions.byPortalCustomer}/${subscriptionId}/add-days`,
    { free_days: freeDays },
  );
  return response.data;
};

export type PauseCycles = 1 | 2 | 3;

export interface PortalPauseOption {
  cycles: PauseCycles;
  resumes_at: string;
  next_payment_date: string;
}

export interface PortalPauseOptions {
  current_next_payment_date: string | null;
  billing_period_days: number;
  options: PortalPauseOption[];
}

export interface PortalPauseResponse {
  success: boolean;
  paused: boolean;
  pause_cycles: number;
  pause_resumes_at: string;
  subscription_status: string;
  next_payment_date: string | null; // unchanged pre-pause date; pause_resumes_at is when billing resumes
}

export interface PortalPaymentMethod {
  brand: string | null;
  last4: string;
  exp_month: number | null;
  exp_year: number | null;
}

export const getPortalPauseOptions = async (
  subscriptionId: string,
): Promise<PortalPauseOptions> => {
  const response = await axiosPortal.get(
    `${apiUrls.subscriptions.byPortalCustomer}/${subscriptionId}/pause-options`,
  );
  return response.data;
};

export const pausePortalSubscription = async (
  subscriptionId: string,
  cycles: PauseCycles,
): Promise<PortalPauseResponse> => {
  const response = await axiosPortal.post(
    `${apiUrls.subscriptions.byPortalCustomer}/${subscriptionId}/pause`,
    { cycles },
  );
  return response.data;
};

// null when the backend has no card to show (it answers with a null body).
export const getPortalPaymentMethod = async (
  subscriptionId: string,
): Promise<PortalPaymentMethod | null> => {
  const response = await axiosPortal.get(
    `${apiUrls.subscriptions.byPortalCustomer}/${subscriptionId}/payment-method`,
  );
  return response.data;
};

// Per-page visual overrides. All colors are nullable: null/undefined = inherit the
// store's customer-portal theme (which falls back to a built-in default).
export interface CancellationPageStyle {
  background_color?: string | null;
  headline_color?: string | null;
  body_color?: string | null; // body text (default for sub-label + step too)
  subscription_label_color?: string | null; // null = inherit body color
  step_indicator_color?: string | null; // null = inherit body color
  primary_button_color?: string | null;
  primary_button_text_color?: string | null;
  secondary_button_color?: string | null;
  secondary_button_text_color?: string | null;
  accent_color?: string | null; // reason page: selected-reason border/fill
  cancel_button_color?: string | null; // reason page: the "continue to cancel" button
  cancel_button_text_color?: string | null;
  headline_align?: 'left' | 'center' | 'right' | null; // also governs the reason question
  body_align?: 'left' | 'center' | 'right' | null; // also governs the extra text blocks
  // Subscription product card (show_subscription_label toggles the whole card).
  product_card_position?: 'above_headline' | 'below_body' | null; // null = above_headline
  product_card_name_color?: string | null;
  product_card_price_color?: string | null;
  product_card_discount_color?: string | null;
  product_card_meta_color?: string | null;
  product_card_bg_color?: string | null;
  product_card_border_color?: string | null;
  show_subscription_label?: boolean; // toggles the subscription product card
  show_step_indicator?: boolean;
  show_body?: boolean;
}

// An independent extra paragraph below the body — owns its alignment + color.
export interface CancellationTextBlock {
  text: string;
  align?: 'left' | 'center' | 'right' | null;
  color?: string | null;
}

interface CancellationImageFields {
  banner_image_url?: string | null;
  image_fit?: 'cover' | 'contain';
  image_size?: number; // % of the image area, only applies when image_fit is "contain"
  body_image_url?: string | null;
  body_image_fit?: 'cover' | 'contain';
  body_image_size?: number;
  extra_text_blocks?: CancellationTextBlock[];
}

export interface CancellationOfferBeforeYouGo extends CancellationImageFields {
  headline: string;
  body: string | null;
  video_url?: string | null; // YouTube or Vimeo URL; autoplays in the retention modal
  style?: CancellationPageStyle;
}

export type FrequencyUnit = 'days' | 'weeks' | 'months' | 'years';

export interface CancellationFrequencyOption {
  option_id: string;
  frequency_value: number;
  frequency_unit: FrequencyUnit;
}

export interface CancellationOfferReason extends CancellationImageFields {
  question: string;
  reasons: string[];
  allow_keep_option: boolean;
  style?: CancellationPageStyle;
}

// A change-frequency page's eligibility from the offer GET's database-only precheck.
// "check": ask the options endpoint; "unavailable": skip the page (reason = the code).
export interface CancellationFrequencyAvailability {
  status: 'check' | 'unavailable';
  reason: string | null;
}

// once = the next renewal only; repeating = renewals within discount_duration_months months
// of the accept (months, not renewals: a weekly plan gets about 13 in 3 months); forever.
export type CancellationDiscountDuration = 'once' | 'repeating' | 'forever';

export interface CancellationOfferPage extends CancellationImageFields {
  offer_page_id?: string | null;
  offer_type: 'pause' | 'change_frequency' | 'discount' | 'downgrade';
  enabled?: boolean;
  pause_cycles: number;
  frequency_options?: CancellationFrequencyOption[]; // change_frequency pages only
  availability?: CancellationFrequencyAvailability; // change_frequency pages only
  // Discount pages only: a percentage off future renewals (1..100), applied by the backend
  // as a Whop promo code. The code itself is never sent to the portal.
  discount_type?: 'percentage' | null;
  discount_percentage?: number | null;
  discount_duration?: CancellationDiscountDuration | null;
  discount_duration_months?: number | null; // "repeating" only (2..24)
  headline: string;
  body?: string | null;
  accept_label?: string | null;
  decline_label?: string | null;
  style?: CancellationPageStyle;
}

export interface CancellationOfferResponse {
  offer: {
    id: string;
    before_you_go: CancellationOfferBeforeYouGo | null;
    offer_page: CancellationOfferPage | null; // the pause page only (older bundles)
    // Every offer page the "offer" token may show, in stored order (currently at most one).
    // Absent from older backends, which send only offer_page.
    offer_pages?: CancellationOfferPage[];
    cancellation_reason: CancellationOfferReason | null;
    // Order of the present+enabled pages (Before-you-go pinned first by the editor).
    page_order?: CancellationRetentionPage[];
  } | null;
}

export type CancellationRetentionPage = 'before_you_go' | 'cancellation_reason' | 'offer';
export type CancellationOfferOutcome =
  | 'retained_no_cancel'
  | 'accepted_pause'
  | 'accepted_change_frequency'
  | 'accepted_discount'
  | 'proceeded_to_cancel'
  | 'abandoned';

// One page the customer walked, for the per-page funnel: a view, or (skipped_reason set)
// a skip report for a change-frequency page the walk passed over. The backend keeps a
// skip only when offer_page_id names the page explicitly.
export interface PageViewReport {
  page: CancellationRetentionPage;
  offer_page_id?: string;
  skipped_reason?: string;
}
// A bare page token is a view (the format older bundles send).
export type PageViewEntry = CancellationRetentionPage | PageViewReport;

export const getCancellationOffer = async (
  subscriptionId: string,
): Promise<CancellationOfferResponse> => {
  const response = await axiosPortal.get(
    `${apiUrls.subscriptions.byPortalCustomer}/${subscriptionId}/cancellation-offer`,
  );
  return response.data;
};

// Errors carry no codes, only a fixed `detail`; callers branch on the HTTP status.
export const submitCancellationOfferResponse = async (
  subscriptionId: string,
  payload: {
    cancellation_offer_id: string;
    selected_reason?: string | null;
    outcome: CancellationOfferOutcome;
    page?: CancellationRetentionPage;
    // The offer page the outcome happened on (sent whenever page is "offer").
    offer_page_id?: string;
    pages_viewed?: PageViewEntry[];
    // Required (with offer_page_id) when outcome is 'accepted_change_frequency'.
    frequency_option_id?: string;
    billing_period_days?: number;
  },
) => {
  const response = await axiosPortal.post(
    `${apiUrls.subscriptions.byPortalCustomer}/${subscriptionId}/cancellation-offer/resolve`,
    payload,
  );
  return response.data;
};

export interface FrequencyOptionsResponse {
  eligible: boolean;
  ineligible_reason: string | null;
  current: {
    billing_period_days: number;
    frequency_value: number;
    frequency_unit: FrequencyUnit;
    next_payment_date: string;
  } | null;
  // Only frequencies longer than the current one, sorted by days. Values are normalized to
  // the largest exact unit; dates are YYYY-MM-DDTHH:MM:SSZ.
  options: Array<
    CancellationFrequencyOption & { billing_period_days: number; next_payment_date: string }
  >;
}

// Every eligibility answer is a 200 (eligible: false with a code, or the options); only
// 403, 404 and 422 (a missing param) are not.
export const getCancellationFrequencyOptions = async (
  subscriptionId: string,
  { cancellationOfferId, offerPageId }: { cancellationOfferId: string; offerPageId: string },
): Promise<FrequencyOptionsResponse> => {
  const response = await axiosPortal.get(
    `${apiUrls.subscriptions.byPortalCustomer}/${subscriptionId}/cancellation-offer/frequency-options`,
    {
      params: {
        cancellation_offer_id: cancellationOfferId,
        offer_page_id: offerPageId,
      },
    },
  );
  return response.data;
};

export interface PortalOrder {
  id: string;
  order_number: string;
  created_at: string;
  total_price: number;
  currency: string;
  status: string;
  is_recurring: boolean;
}

export interface PortalOrdersResponse {
  orders: PortalOrder[];
  total: number;
  page: number;
  page_size: number;
}

export const getPortalOrders = async (params: {
  page?: number;
  page_size?: number;
}): Promise<PortalOrdersResponse> => {
  const response = await axiosPortal.get(apiUrls.orders.byPortalCustomer, {
    params,
  });
  return response.data;
};

export interface PortalOrderLineItem {
  id: string;
  name: string;
  image: string | null;
  color: string | null;
  size: string | null;
  quantity: number;
  price: number;
  selected_options: LineOption[]; // normalized in useOrder; the wire may send a legacy dict
}

export interface PortalShippingAddress {
  line1: string;
  line2: string | null;
  city: string;
  state: string | null;
  postal_code: string;
  country: string;
}

export interface PortalOrderDetail {
  id: string;
  order_number: string;
  created_at: string;
  status: string;
  currency: string;
  subtotal: number;
  shipping: number;
  tax: number;
  discount: number;
  total: number;
  is_recurring: boolean;
  line_items: PortalOrderLineItem[];
  shipping_address: PortalShippingAddress | null;
}

export interface PortalAccount {
  name: string | null;
  email: string;
  shipping_address: PortalShippingAddress | null;
}

export const getPortalAccount = async (): Promise<PortalAccount> => {
  const response = await axiosPortal.get(apiUrls.customerPortal.account);
  return response.data;
};

export const getPortalOrder = async (orderId: string): Promise<PortalOrderDetail> => {
  const response = await axiosPortal.get(`${apiUrls.orders.byPortalCustomer}/${orderId}`);
  return response.data;
};
